import { createContext, useCallback, useMemo, useRef, useState } from 'react';

// Context
import { useTranslation } from '/utilities/i18n';

// Services
import menuServices, { findMenuCategory } from '/services/menu';

export const MenuContext = createContext({
	getMenu: () => {},
	getMenuCategory: () => {},
	loadMenu: () => {},
	loadMenuCategory: () => {},
});

export const MenuProvider = ({ children }) => {
	const { t, language } = useTranslation();
	const requestsRef = useRef({});
	const [menus, setMenus] = useState({});

	// API, menüyü `language` header'ına göre döndürdüğü için önbellek dile göre ayrılır.
	const getKey = useCallback((branchSlug) => `${language}:${branchSlug}`, [language]);

	const getMenu = useCallback(
		(branchSlug) => {
			return (branchSlug && menus[getKey(branchSlug)]) || null;
		},
		[menus, getKey]
	);

	const loadMenu = useCallback(
		(branchSlug) => {
			const key = getKey(branchSlug);

			if (!requestsRef.current[key]) {
				requestsRef.current[key] = menuServices
					.getMenu(branchSlug)
					.then((menu) => {
						setMenus((menus) => ({ ...menus, [key]: menu }));

						return menu;
					})
					.catch((feedback) => {
						// Hatalı istek önbellekte kalmasın, sonraki ziyarette tekrar denensin.
						delete requestsRef.current[key];

						return Promise.reject(feedback);
					});
			}

			return requestsRef.current[key];
		},
		[getKey]
	);

	const getMenuCategory = useCallback(
		(branchSlug, categoryId) => {
			return findMenuCategory(getMenu(branchSlug), categoryId);
		},
		[getMenu]
	);

	const loadMenuCategory = useCallback(
		(branchSlug, categoryId) => {
			return loadMenu(branchSlug).then((menu) => {
				const category = findMenuCategory(menu, categoryId);

				if (category) {
					return category;
				}

				return Promise.reject(t('Menü getirilirken bir hata ile karşılaşıldı, lütfen tekrar dene.'));
			});
		},
		[loadMenu, t]
	);

	const api = useMemo(() => {
		return {
			getMenu,
			getMenuCategory,
			loadMenu,
			loadMenuCategory,
		};
	}, [getMenu, getMenuCategory, loadMenu, loadMenuCategory]);

	return <MenuContext.Provider value={api}>{children}</MenuContext.Provider>;
};
